/* eslint-disable react/jsx-max-depth */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */

import { ChronicDiseaseSimple } from '@dreryk/egabinet-patient-medical-information-frontend-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { appendTestableElementAttributes, ControlsBar, StyledFlex } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { ReceptionServicesDetails } from '~libs.shared.reception-service';
import { createPatientWithExternalIdentifierProtector } from '~libs.shared.visit';
import { useGetCurrentPractitionerGlobalState } from '~libs.shared.account';
import { PatientEntitlementToMedicalServiceStatus } from '~libs.desktop.patient-entitlement-to-medical-service';
import { PatientGroups } from '~libs.desktop.shared.patient-groups';
import { TimeSinceLastVisit, TimeSinceLastVisitLoader } from '~libs.desktop.shared.visit';

import { ResourceByPatientGenderGuard } from '../common/ResourceByPatientGenderGuard';
import { PrescriptionVisitHeader } from '../common/visit/header/PrescriptionVisitHeader';
import { VisitHeaderActions } from '../common/visit/header/VisitHeaderActionsPortal';
import { VisitHeaderPatientDetails } from '../common/visit/header/VisitHeaderPatientDetails';
import { VisitHeaderPatientNameLink } from '../common/visit/header/VisitHeaderPatientNameLink';
import { VisitDateTime } from '../common/visit/VisitDateTime';
import { VisitStopwatch } from '../common/visit/VisitStopwatch';
import { VisitTimer } from '../common/visit/VisitTimer';
import { PrescriptionIssuingPanel } from '../features/prescriptions';
import { visitTranslationKeys } from '../translations';
import { VisitSidePanel } from '../visitSidePanel/VisitSidePanel';
import { VisitFormContextProvider } from '../common/visitForm/VisitFormContext';
import { SaveAndClosePrescriptionVisitAction } from '../features/prescriptions/prescriptions/draftPrescriptions/SaveAndClosePrescriptionVisitAction';

import { getServiceLocationIdFromVisitLocation } from './visitLocation/getServiceLocationIdFromVisitLocation';
import { VisitActionBar } from './actionBar/VisitActionBar';
import { VisitFormLoader } from './form/VisitForm.loader';
import { PatientPregnancyStatus } from './patientPregnancy/PatientPregnancyStatus';
import { PrescriptionVisitDataAction } from './prescriptionVisitData/PrescriptionVisitDataAction';
import { VisitLocation } from './visitLocation/VisitLocation';
import { StyledVisitPanel } from './VisitPanel.styled';
import { clockVisitor } from './VisitPanel.utils';
import { VisitRouteChangingGuard } from './VisitRouteChangingGuard';
import { getPrescriptionVisitPath } from './PrescriptionOnlyVisitPanel.utils';

type PrescriptionOnlyVisitPanelProps = {
   readonly visit: Visit;
};

type PrescriptionOnlyVisitPanelContentProps = {
   readonly visit: Visit;
   readonly chronicDiseases: ChronicDiseaseSimple[];
};

const PrescriptionOnlyVisitPanelContent = (props: PrescriptionOnlyVisitPanelContentProps): JSX.Element => {
   const protectByPatientWithExternalIdentifier = createPatientWithExternalIdentifierProtector(props.visit.patient);
   const currentPractitioner = useGetCurrentPractitionerGlobalState();

   return (
      <VisitActionBar>
         <VisitRouteChangingGuard />
         <StyledVisitPanel {...appendTestableElementAttributes('visitPanel')}>
            <PrescriptionVisitHeader
               title={
                  <StyledFlex gap={4} alignItems={'center'}>
                     <VisitHeaderPatientNameLink
                        patient={props.visit.patient}
                        backActionLabel={visitTranslationKeys.goBackToVisit}
                     />
                     <PatientGroups patientGroups={props.visit.patient.patientGroups} />
                  </StyledFlex>
               }
               details={
                  <>
                     <VisitHeaderPatientDetails patient={props.visit.patient} />
                     {protectFeature(
                        'privateMedicalServicesReadOnly',
                        null,
                        <ReceptionServicesDetails appointmentId={props.visit.appointmentId} />
                     )}
                     <TimeSinceLastVisitLoader
                        patientId={props.visit.patient.id}
                        practitionerId={currentPractitioner.id}
                        excludedVisitId={props.visit.id}
                     >
                        {({ timeSinceLast }) => (
                           <TimeSinceLastVisit
                              timeSinceLast={timeSinceLast}
                              getVisitPath={getPrescriptionVisitPath}
                              backActionLabel={visitTranslationKeys.goBackToPrescriptionVisit}
                           />
                        )}
                     </TimeSinceLastVisitLoader>
                  </>
               }
               actions={
                  <VisitHeaderActions.Container>
                     {clockVisitor(props.visit)({
                        timer: ({ beginDate, plannedBeginDate, plannedEndDate }) => (
                           <VisitTimer
                              beginDate={beginDate}
                              plannedBeginDate={plannedBeginDate}
                              plannedEndDate={plannedEndDate}
                           />
                        ),

                        stopwatch: ({ beginDate }) => <VisitStopwatch beginDate={beginDate} />,
                     })}
                  </VisitHeaderActions.Container>
               }
            />
            <ControlsBar testId={'visit'}>
               <ControlsBar.Main position={'left'}>
                  <VisitDateTime
                     plannedBeginDate={props.visit.plannedBeginDate}
                     plannedEndDate={props.visit.plannedEndDate}
                  />
                  <VisitLocation
                     visitId={props.visit.id}
                     disabled={!props.visit.cancellable}
                     {...props.visit.visitLocation}
                  />
               </ControlsBar.Main>

               <VisitHeaderActions.Portal>
                  <PatientEntitlementToMedicalServiceStatus
                     visitId={props.visit.id}
                     checkDate={props.visit.beginDate}
                     readonly={props.visit.reopened}
                     patientReferenceIdentifier={props.visit.patient.referenceIdentifier}
                  />
                  <ResourceByPatientGenderGuard patientId={props.visit.patient.id} gender={'WOMAN'}>
                     <PatientPregnancyStatus
                        pregnancyStatus={props.visit.visitDetails.pregnancyStatus}
                        visitId={props.visit.id}
                     />
                  </ResourceByPatientGenderGuard>
               </VisitHeaderActions.Portal>

               {protectByPatientWithExternalIdentifier(
                  'issuingPrescriptions',
                  <PrescriptionIssuingPanel
                     patientId={props.visit.patient.id}
                     patientAddress={props.visit.patient.address}
                     patientReferenceIdentifier={props.visit.patient.referenceIdentifier!}
                     locationId={getServiceLocationIdFromVisitLocation(props.visit.visitLocation)}
                     visitId={props.visit.id}
                     visitDiagnoses={props.visit.diagnoses}
                     appointmentId={props.visit.appointmentId}
                     inPersonExaminationPerformed={Boolean(props.visit.visitDetails.inPersonExaminationPerformed)}
                     documentationScope={props.visit.documentationScope}
                  />
               )}
            </ControlsBar>
         </StyledVisitPanel>
         <VisitSidePanel
            patientId={props.visit.patient.id}
            currentVisitId={props.visit.id}
            visitDiagnoses={props.visit.diagnoses}
         />
         <PrescriptionVisitDataAction visit={props.visit} chronicDiseases={props.chronicDiseases} />
         <SaveAndClosePrescriptionVisitAction />
      </VisitActionBar>
   );
};

export const PrescriptionOnlyVisitPanel = (props: PrescriptionOnlyVisitPanelProps): JSX.Element => {
   return (
      <VisitFormContextProvider>
         <VisitFormLoader patientId={props.visit.patient.id}>
            {({ chronicDiseases }) => (
               <PrescriptionOnlyVisitPanelContent visit={props.visit} chronicDiseases={chronicDiseases} />
            )}
         </VisitFormLoader>
      </VisitFormContextProvider>
   );
};
